import { useDataChainId } from '@ui/hooks/datachain';
import { usePriceMapLatest } from '@ui/hooks/price/usePriceLatest';
import { Order } from '@ui/hooks/trade/useTradeOrders/types';
import {
  getPayTokenPriceMapKey,
  usePayTokenPriceLatestMap,
} from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { OrderType } from '@ui/types/trade';
import { TradePair } from '@ui/types/tradePair';
import { Nullable } from '@ui/types/utility';
import { getProperLeverageMargin } from '@ui/utils/trade/getProperLeverageMargin';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import type {
  IChartWidgetApi,
  IOrderLineAdapter,
  IPositionLineAdapter,
} from 'trading-view';

import { useTradePairInfos } from '../../../../hooks/trade/useTradePairInfos';
import {
  drawLiqPriceLine,
  drawOpenOrderLine,
  drawPositionLine,
  drawStopLossLine,
  drawTakeProfitLine,
} from './utils/drawLines';

export type UseDrawLinesOptions = {
  tradingPair: TradePair;
  chartApi: Nullable<IChartWidgetApi>;
  isChartReady: boolean;
  tpSl?: {
    isLong: boolean;
    takeProfit: Nullable<BigNumber>;
    stopLoss: Nullable<BigNumber>;
  }[];
  orders?: Order[];
  positions?: Position[];
  openTrade?: {
    orderType: OrderType;
    isLong: boolean;
    entryPrice: Nullable<BigNumber>;
    liqPrice: Nullable<BigNumber>;
  };
};
export const useDrawLines = ({
  chartApi,
  orders,
  positions,
  tpSl,
  openTrade,
  isChartReady,
}: UseDrawLinesOptions) => {
  const chainId = useDataChainId();
  const { t } = useTranslation();
  const { data: priceMap } = usePriceMapLatest();
  const collateralPriceMap = usePayTokenPriceLatestMap(
    positions?.map((position) => position.collateralToken),
  );
  const pairInfos = useTradePairInfos();

  // order, position, tpsl
  useEffect(() => {
    if (!chartApi || !isChartReady) return;
    let lines: (IPositionLineAdapter | IOrderLineAdapter)[] = [];
    try {
      const orderLines = (orders ?? []).map((order) => {
        return drawOpenOrderLine({
          t,
          chartApi,
          order,
        });
      });
      const positionLines = (positions ?? [])
        .filter((position) => !position.isPendingPosition)
        .map((position) => {
          return drawPositionLine({
            t,
            chartApi,
            position: {
              entryPrice: position.entryPrice,
              isLong: position.isLong,
            },
          });
        });

      const tpSlLines = (tpSl ?? []).flatMap((price) => {
        const tpSlLineList = [];
        if (price.takeProfit) {
          tpSlLineList.push(
            drawTakeProfitLine({
              chartApi,
              takeProfit: {
                isLong: price.isLong,
                triggerPrice: price.takeProfit,
              },
            }),
          );
        }
        if (price.stopLoss) {
          tpSlLineList.push(
            drawStopLossLine({
              chartApi,
              stopLoss: {
                isLong: price.isLong,
                triggerPrice: price.stopLoss,
              },
            }),
          );
        }

        return tpSlLineList;
      });

      lines = [...orderLines, ...positionLines, ...tpSlLines];
    } catch (e) {
      console.error(e);
    }

    return () => {
      lines.forEach((line) => line.remove());
    };
  }, [t, chainId, chartApi, positions, orders, tpSl, isChartReady]);

  // position liq price
  useEffect(() => {
    if (!chartApi || !priceMap || !isChartReady) return;
    let lines: (IPositionLineAdapter | IOrderLineAdapter)[] = [];
    try {
      const liqPriceLines = (positions ?? [])
        .map((position) => {
          const pairInfo = pairInfos?.find(
            (v) =>
              v.address.toLowerCase() === position.pair.address.toLowerCase(),
          );
          const collateralPrice =
            collateralPriceMap[getPayTokenPriceMapKey(position.collateralToken)];
          const leverage = collateralPrice
            ? position.getLeverage({
                collateralTokenMarketPrice: collateralPrice,
              })
            : undefined;
          const leverageMargin = pairInfo?.leverageMargins;

          const liqLostP =
            leverageMargin && leverage
              ? getProperLeverageMargin(leverageMargin, leverage.toNumber())
                  ?.liqLostP
              : undefined;
          const pairPrice = priceMap[position.pair.baseSymbol];
          if (!collateralPrice || !pairInfo?.feeConfig || !pairPrice) return [];
          const liqPrice = position.getLiquidationPrice({
            collateralMarketPrice: collateralPrice,
            pairMarketPrice: pairPrice,
            feeConfig: pairInfo?.feeConfig,
            liqLostP,
            tradePair: pairInfo,
          });

          if (!liqPrice) return [];

          // hide liquidation line when this position has a Stop Loss set
          const hasStopLoss = !position.stopLoss.isZero();
          if (hasStopLoss) return [];

          return drawLiqPriceLine({
            t,
            chartApi,
            isLong: position.isLong,
            liqPrice: liqPrice,
          });
        })
        .flat();
      lines = liqPriceLines;
    } catch (e) {
      console.error(e);
    }

    return () => {
      lines.forEach((line) => line.remove());
    };
  }, [
    chartApi,
    collateralPriceMap,
    isChartReady,
    pairInfos,
    positions,
    priceMap,
    t,
  ]);

  // openTrade
  useEffect(() => {
    if (!chartApi || !openTrade || !isChartReady) return;
    let lines: (IPositionLineAdapter | IOrderLineAdapter)[] = [];
    try {
      // hide liquidation line when there is a Stop Loss configured for the same side
      const hasStopLossForOpenTrade = (tpSl ?? []).some(
        (p) => p.isLong === openTrade.isLong && !!p.stopLoss,
      );

      if (openTrade.liqPrice && !hasStopLossForOpenTrade) {
        lines.push(
          drawLiqPriceLine({
            chartApi,
            isLong: openTrade.isLong,
            t,
            liqPrice: openTrade.liqPrice,
          }),
        );
      }
      if (openTrade.orderType === OrderType.MARKET && openTrade.entryPrice) {
        lines.push(
          drawPositionLine({
            chartApi,
            position: {
              entryPrice: openTrade.entryPrice,
              isLong: openTrade.isLong,
            },
            t,
          }),
        );
      } else if (
        openTrade.orderType === OrderType.LIMIT &&
        openTrade.entryPrice
      ) {
        lines.push(
          drawOpenOrderLine({
            chartApi,
            order: {
              triggerPrice: openTrade.entryPrice,
              isLong: openTrade.isLong,
            },
            t,
          }),
        );
      }
    } catch (e) {
      console.error(e);
    }

    return () => {
      lines.forEach((line) => line.remove());
    };
  }, [chartApi, isChartReady, openTrade, t, tpSl]);
};
